import { Scanner as ScannerType } from "@/lib/types/scanner.ts";
import { Fragment, useState } from "react";

import { useTimelapseCreate, useTimelapseUpdate } from "@/lib/api/timelapse";
import { Area } from "@/lib/types/area";
import { convertTimelapseModel, TimelapseCreateSchema, TimelapseStatus as TimelapseStatusEnum } from "@/lib/types/timelapse";
import { ActionIcon } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { FaChevronDown, FaPencil, FaRegLightbulb } from "react-icons/fa6";
import { IoMdQrScanner } from "react-icons/io";
import { ModalCenter } from "../atoms/ModalCenter";
import { Confirm } from "../molecules/Confirm";
import { TimelapseFormCreate } from "../timelapse/TimelapseFormCreate";
import { TimelapseStatus } from "../timelapse/TimelapseStatus";
import { ScannerArea } from "./ScannerArea";

import { useScannerLight } from "@/lib/api/scanner";
import { AnimatePresence, motion } from "framer-motion";
import { formatInterval } from "@/lib/utils";
import { CardGray } from "../atoms/CardGray";

type Props = {
  scanner: ScannerType,
  onEdit: (node: ScannerType) => void;
}

export const Scanner = ({ scanner, onEdit }: Props) => {
  const [expanded, setExpanded] = useState(false)

  const [openedStop, { open: openStop, close: closeStop }] = useDisclosure()
  const [openedNew, { open: openNew, close: closeNew }] = useDisclosure()
  const [selected, setSelected] = useState<Area | undefined>()

  const timelapseCreate = useTimelapseCreate()
  const timelapseUpdate = useTimelapseUpdate()

  const [openedLight, { open: openLight, close: closeLight }] = useDisclosure()
  const scannerLight = useScannerLight()

  const handleTimelapseCreateInit = (area: Area) => {
    setSelected(area)
    openNew()
  }
  const handleTimelapseCreate = (timelapse: TimelapseCreateSchema) => {
    if (!selected) return

    if (selected.timelapse) {
      const oldTimelapse = convertTimelapseModel(selected.timelapse)
      oldTimelapse.status = TimelapseStatusEnum.FINISHED

      timelapseUpdate.mutate(oldTimelapse, {
        onError: (err) => notifications.show({ variant: "error", message: err.message }),
      })
    }

    timelapseCreate.mutate(timelapse, {
      onSuccess: () => notifications.show({ variant: "success", message: "Timelapse created" }),
      onError: (err) => notifications.show({ variant: "error", message: err.message }),
      onSettled: () => closeNew(),
    })
  }

  const handleTimelapseStatusUpdate = (area: Area, status: TimelapseStatusEnum) => {
    if (!area.timelapse) return

    const timelapse = convertTimelapseModel(area.timelapse)
    timelapse.status = status

    timelapseUpdate.mutate(timelapse, {
      onSuccess: () => notifications.show({ variant: "success", message: "Timelapse updated" }),
      onError: (err) => notifications.show({ variant: "error", message: err.message }),
      onSettled: () => closeStop(),
    })
  }

  const handleTimelapseDelete = (area: Area) => {
    setSelected(area)
    openStop()
  }

  const handleLight = () => {
    openLight()
  }

  const handleLightConfirm = () => {
    scannerLight.mutate(scanner, {
      onSuccess: () => notifications.show({ variant: "success", message: "Scanner is lightning up, go find it!" }),
      onSettled: () => closeLight(),
    })
  }

  return (
    <>
      <CardGray id={`scanner-${scanner.id}`} className="flex flex-col gap-4 h-fit">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IoMdQrScanner />
            <span className="font-bold">{scanner.name}</span>
            <span className="text-muted">ID {scanner.id}</span>
          </div>
        </div>
        <div className="text-muted text-center">
          {scanner.areas.length > 0 ? (
            <div className="grid grid-cols-[max-content_1fr_max-content] items-center space-x-2 space-y-1 text-sm text-start">
              {scanner.areas.map((a, idx) => (
                <Fragment key={a.id}>
                  <div>{`Area ${idx + 1}`}</div>
                  <div className="border-l border-muted pl-2">{`Interval ${formatInterval(a.intervalS)}`}</div>
                  {a.timelapse
                    ? <TimelapseStatus status={a.timelapse.status} noText />
                    : <div />
                  }
                </Fragment>
              ))}
            </div>
          ) : (
            <span>No areas defined</span>
          )}
        </div>
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              style={{ overflow: "hidden" }}
            >
              <div className="grid md:grid-cols-2 gap-4 pt-4">
                {scanner.areas.map((a, idx) => (
                  <ScannerArea
                    key={a.id}
                    area={a}
                    idx={idx}
                    onTimelapseCreate={handleTimelapseCreateInit}
                    onTimelapseUpdate={handleTimelapseStatusUpdate}
                    onTimelapseDelete={handleTimelapseDelete}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="flex gap-2 justify-end">
          {expanded && (
            <>
              <ActionIcon variant="transparent" color="black" onClick={handleLight} disabled={false}>
                <FaRegLightbulb />
              </ActionIcon>
              <ActionIcon variant="transparent" color="black" onClick={() => onEdit(scanner)}>
                <FaPencil />
              </ActionIcon>
            </>
          )}
          <ActionIcon
            variant="transparent"
            color="black"
            onClick={() => setExpanded(prev => !prev)}
            className="transition-transform duration-200"
            style={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)"
            }}
          >
            <FaChevronDown />
          </ActionIcon>
        </div>
      </CardGray>
      <ModalCenter
        opened={openedNew}
        onClose={closeNew}
        title="New timelapse"
      >
        <TimelapseFormCreate areaId={selected?.id ?? 0} currentTimelapse={selected?.timelapse} close={closeNew} onSubmit={handleTimelapseCreate} />
      </ModalCenter>
      <Confirm
        opened={openedStop}
        onClose={closeStop}
        modalTitle={selected?.timelapse?.name ?? ""}
        title="Are you sure you want to stop this timelapse?"
        description="You won't be able to resume it again."
        onConfirm={() => handleTimelapseStatusUpdate(selected!, TimelapseStatusEnum.FINISHED)}
      />
      <Confirm
        opened={openedLight}
        onClose={closeLight}
        modalTitle={scanner.name}
        title="Light up scanner"
        description="This will light up the scanner so you can identify which scanner it is"
        onConfirm={handleLightConfirm}
      />
    </>
  )
}
